import React, { useState } from 'react';
import { API_BASE_URL } from "../config";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import './SignIn.css';

const SignIn = ({ onSuccessfulLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log(`Attempting to ${mode} with:`, formData.email);
      const response = await fetch(`${API_BASE_URL}/${mode}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Login response:', data);
      
      if (data.status === "success") {
        console.log("✅ Login successful, saving user data");
        const userData = {
          email: formData.email,
          isAuthenticated: true,
          is_admin: data.is_admin || false
        };

        if (formData.rememberMe) {
          localStorage.setItem("rememberedEmail", formData.email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        if (typeof onSuccessfulLogin === 'function') {
          onSuccessfulLogin(userData);
        } else {
          localStorage.setItem("user", JSON.stringify(userData));
        }
      } else {
        throw new Error(data.message || "Authentication failed");
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || "Failed to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      console.log("Google Login Success:", decoded);
      
      const userData = {
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        isAuthenticated: true,
        is_admin: false,
        isGoogleAuth: true
      };

      localStorage.setItem("user", JSON.stringify(userData));
      
      if (typeof onSuccessfulLogin === 'function') {
        onSuccessfulLogin(userData);
      }
    } catch (err) {
      console.error("Failed to decode Google JWT", err);
      setError("Google Login failed. Please try again.");
    }
  };

  const handleGoogleError = () => {
    console.error("Google Login Failed");
    setError("Google Login failed. Please try again.");
  };

  return (
    <div className="si-page">
      {/* ═══ LEFT PANEL — Visual Storytelling ═══ */}
      <div className="si-left">
        {/* Logo */}
        <div className="si-logo">
          <div className="si-logo-icon">S4</div>
          <span className="si-logo-text">stocks4u</span>
        </div>

        {/* Heading */}
        <div className="si-heading">
          <h1>
            Smarter insights.<br />
            <span className="si-highlight">Better</span> decisions.
          </h1>
        </div>

        {/* Subtext */}
        <p className="si-subtext">
          Real-time market data, powerful analytics, and portfolio insights — all in one place.
        </p>

        {/* Floating Analytics Card */}
        <div className="si-analytics-card">
          <div className="si-card-label">Portfolio Value</div>
          <div className="si-card-value">$24,735.64</div>
          <div className="si-card-change">
            <span>▲</span> +6.32% Today
          </div>

          {/* SVG Line Chart */}
          <div className="si-chart-container">
            <svg viewBox="0 0 320 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7B61FF" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#7B61FF" stopOpacity="0" />
                </linearGradient>
                <filter id="chartGlow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {/* Area fill */}
              <path
                d="M0,70 Q30,65 60,55 T120,40 T180,45 T240,25 T300,18 L320,12 L320,80 L0,80 Z"
                fill="url(#chartGrad)"
              />
              {/* Glow stroke line */}
              <path
                d="M0,70 Q30,65 60,55 T120,40 T180,45 T240,25 T300,18 L320,12"
                stroke="#7B61FF"
                strokeWidth="2.5"
                fill="none"
                filter="url(#chartGlow)"
                strokeLinecap="round"
              />
              {/* End dot */}
              <circle cx="320" cy="12" r="4" fill="#7B61FF">
                <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="320" cy="12" r="8" fill="#7B61FF" opacity="0.2">
                <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

          {/* Stats */}
          <div className="si-card-stats">
            <div className="si-stat-item">
              <div className="si-stat-label">Holdings</div>
              <div className="si-stat-value">28</div>
            </div>
            <div className="si-stat-item">
              <div className="si-stat-label">Watchlist</div>
              <div className="si-stat-value">16</div>
            </div>
            <div className="si-stat-item">
              <div className="si-stat-label">Alerts</div>
              <div className="si-stat-value">7</div>
            </div>
          </div>
        </div>

        {/* Security Badge */}
        <div className="si-security-badge">
          <div className="si-badge-icon">🛡️</div>
          <div className="si-badge-text">
            <h4>Bank-level security</h4>
            <p>256-bit encryption • Secure • Private</p>
          </div>
        </div>
      </div>

      {/* ═══ RIGHT PANEL — Sign In Form ═══ */}
      <div className="si-right">
        <GoogleOAuthProvider clientId="YOUR_GOOGLE_CLIENT_ID">
          <div className="si-form-card">
            <h2 className="si-form-title">
              {mode === "login" ? "Welcome back" : "Create account"}
            </h2>
            <p className="si-form-subtitle">
              {mode === "login"
                ? "Sign in to continue to your account"
                : "Get started with your free account"}
            </p>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="si-input-group">
                <label className="si-input-label">Email address</label>
                <div className="si-input-wrapper">
                  <span className="si-input-icon">✉</span>
                  <input
                    type="email"
                    name="email"
                    className="si-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="si-input-group">
                <label className="si-input-label">Password</label>
                <div className="si-input-wrapper">
                  <span className="si-input-icon">🔒</span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="si-input"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: '48px' }}
                  />
                  <button
                    type="button"
                    className="si-password-toggle"
                    onClick={() => setShowPassword(prev => !prev)}
                    tabIndex={-1}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {/* Options row */}
              {mode === "login" && (
                <div className="si-options-row">
                  <label className="si-checkbox-wrap">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <span className="si-checkmark">
                      <svg className="si-checkmark-tick" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    Remember me
                  </label>
                  <a href="#" className="si-forgot-link" onClick={(e) => e.preventDefault()}>
                    Forgot password?
                  </a>
                </div>
              )}

              {/* Error */}
              {error && <div className="si-error">{error}</div>}

              {/* Submit */}
              <button type="submit" className="si-submit-btn" disabled={loading}>
                {loading
                  ? (mode === "login" ? "Signing In..." : "Creating Account...")
                  : (mode === "login" ? "Sign In" : "Sign Up")}
              </button>
            </form>

            {/* Signup / Signin toggle */}
            <div className="si-signup-text">
              {mode === "login" ? (
                <p>
                  Don't have an account?{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); setMode("signup"); }}>
                    Sign up
                  </a>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); setMode("login"); }}>
                    Sign in
                  </a>
                </p>
              )}
            </div>

            {/* Divider */}
            <div className="si-divider">
              <div className="si-divider-line" />
              <span className="si-divider-text">OR</span>
              <div className="si-divider-line" />
            </div>

            {/* Google Login */}
            <button type="button" className="si-google-btn" onClick={() => {
              // The GoogleLogin component handles its own click — this is a fallback styled button
              const googleBtn = document.querySelector('[data-type="standard"]');
              if (googleBtn) googleBtn.click();
            }}>
              <svg className="si-google-icon" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>

            {/* Hidden actual Google Login for OAuth handling */}
            <div style={{ display: 'none' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                shape="rectangular"
                size="large"
                text="continue_with"
                width="100%"
              />
            </div>

            {/* Privacy footer */}
            <div className="si-privacy-footer">
              🔒 We never share your data with third parties.
            </div>
          </div>
        </GoogleOAuthProvider>
      </div>
    </div>
  );
};

export default SignIn;